/**
 * `vw31tt2bi7@bwmyga.com` -> `vw3***@bwmyga.com`.
 *
 * Enough to recognise which address something went to, not enough to hand the
 * whole thing to whoever is looking at the screen. Every caller is a screen the
 * user can reach from any machine, including a shared one: the two-factor gate,
 * and the page that takes a new password from a reset link.
 *
 * Reveals at most three characters, and fewer on a short local part -- a
 * two-character address masked as `ab***` would be printed in full. Anything
 * without an `@` is masked whole rather than guessed at.
 *
 * Lives here rather than beside its first caller. It was exported from
 * UserProfile's MfaEnrollment, and the second caller is an Auth view -- reaching
 * across into another feature for a pure string helper would put the two
 * features' components in a cycle for twelve lines of code.
 *
 * Returns '' for a missing address, which callers MUST handle: interpolating it
 * into a sentence produced "Enter the 6-digit code sent to ." on the two-factor
 * gate, where the store this read from is never populated. An empty mask is not
 * a mask.
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  const at = email.lastIndexOf('@');
  if (at <= 0) return '***';
  const local = email.slice(0, at);
  const domain = email.slice(at);
  // Always strictly fewer characters than the local part has, so a short
  // address is never printed whole: a one-character local reveals nothing.
  const shown =
    local.length <= 1
      ? 0
      : local.length <= 2
        ? 1
        : Math.min(3, local.length - 1);
  return `${local.slice(0, shown)}***${domain}`;
}

/**
 * `+14385557501` -> `+1 ***7501`.
 *
 * Enough to recognise which handset a code went to, not enough to hand the
 * number to whoever is looking at the screen — the same bargain maskEmail
 * makes, on a screen reached from any machine including a shared one.
 *
 * THE DIAL CODE HAS TO BE PASSED IN. It cannot be recovered from the number:
 * +1 438… and +143 8… are the same digits, and a country code is one to three
 * of them with no marker between. Guessing produced "+143 ***7501" for a
 * Canadian number. The enrolment screen knows it (the user picked it); the
 * sign-in challenge does not, and there the number is shown as ***7501 rather
 * than invented.
 *
 * A value that ALREADY contains `*` is passed through untouched. Identity
 * Platform obfuscates the phone on a sign-in hint, and masking a mask would
 * either double it or slice the asterisks as if they were digits.
 *
 * Not used on the enrolment NUMBER step, deliberately: there the user typed it
 * three seconds ago, so hiding it conceals nothing and takes away their only
 * chance to catch a typo before the code is sent.
 */
export function maskPhone(
  phone: string | null | undefined,
  dial?: string,
): string {
  if (!phone) return '';
  if (phone.includes('*')) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  const last4 = digits.slice(-4);
  return dial ? `+${dial} ***${last4}` : `***${last4}`;
}
